import { Head, Link, router } from "@inertiajs/react";
import { format } from "date-fns";
import { Eye, FileSearch, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { StatusBadge } from "@/bizminer/Admin/InsightRequests/_components/StatusBadge";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout";
import { CompanyLogo } from "@/components/CompanyLogo";
import { ConnectionStatus } from "@/components/ConnectionStatus";
import { Button } from "@/components/ui/button";
import { useAdminInsightRequestsChannel } from "@/hooks/useAdminInsightRequestsChannel";
import {
  bizminer_admin_insight_request_path,
  bizminer_admin_insight_requests_path,
  bizminer_admin_organization_account_path,
  bizminer_admin_organization_path,
} from "@/routes";
import type { PagyMetadata } from "@/types/Pagy";
import type { BizminerAdminInsightRequest } from "@/types/serializers";

interface Props {
  insight_requests: BizminerAdminInsightRequest[];
  pagy: PagyMetadata;
}

const EngineStatusBadge = ({ status }: { status: string | null }) => {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <span className="text-xs text-muted-foreground font-mono">{status}</span>
  );
};

const PaginationControls = ({ pagy }: { pagy: PagyMetadata }) => {
  const navigate = (page: number) => {
    router.get(
      window.location.pathname,
      { page },
      { preserveState: true, preserveScroll: false },
    );
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-border">
      <div className="text-sm text-muted-foreground">
        Showing {pagy.from} to {pagy.to} of {pagy.count} requests
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => navigate(pagy.previous!)}
          disabled={!pagy.previous}
          variant="outline"
          size="sm"
        >
          Previous
        </Button>

        <span className="px-4 py-2 text-sm text-muted-foreground">
          Page {pagy.page} of {pagy.last}
        </span>

        <Button
          onClick={() => navigate(pagy.next!)}
          disabled={!pagy.next}
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default function Index({
  insight_requests: initialRequests,
  pagy,
}: Props) {
  const [requests, setRequests] = useState(initialRequests);

  // Sync state when props change (after partial reload)
  useEffect(() => {
    setRequests(initialRequests);
  }, [initialRequests]);

  // Reload data when navigating back to this page (fixes stale cache)
  useEffect(() => {
    const removeListener = router.on("navigate", (event) => {
      const indexPath = bizminer_admin_insight_requests_path();
      if (event.detail.page.url === indexPath) {
        router.reload({ only: ["insight_requests"] });
      }
    });

    return () => removeListener();
  }, []);

  const handleUpdate = useCallback((updated: BizminerAdminInsightRequest) => {
    setRequests((prev) => {
      const exists = prev.some((r) => r.id === updated.id);
      if (exists) {
        // Update existing item in place
        return prev.map((r) => (r.id === updated.id ? updated : r));
      } else {
        // New item - trigger partial reload to fetch fresh list
        router.reload({ only: ["insight_requests"] });
        return prev;
      }
    });
  }, []);

  const { isConnected } = useAdminInsightRequestsChannel(handleUpdate);

  return (
    <BizminerAdminLayout>
      <Head title="Insight Requests - Super Admin - SalesMinr" />
      <div className="min-h-screen bg-background">
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Loader2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    Insight Requests
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    View and manage all insight requests across organizations
                  </p>
                </div>
              </div>
              <ConnectionStatus
                status={isConnected ? "connected" : "disconnected"}
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-card shadow rounded-lg">
              {requests.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <FileSearch className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p>No insight requests found</p>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Organization
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Account
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Pillar
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Engine Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {requests.map((request) => (
                        <tr key={request.id} className="hover:bg-muted/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <StatusBadge status={request.status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              href={bizminer_admin_organization_path(
                                request.organization.id,
                              )}
                              className="text-sm font-medium text-primary hover:text-primary/80"
                            >
                              {request.organization.name}
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <CompanyLogo
                                logoUrl={request.account.logo_url}
                                name={request.account.name}
                                className="h-6 w-6"
                              />
                              <Link
                                href={bizminer_admin_organization_account_path(
                                  request.organization.id,
                                  request.account.id,
                                )}
                                className="text-sm text-primary hover:text-primary/80"
                              >
                                {request.account.name}
                              </Link>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-secondary text-secondary-foreground">
                              {request.pillar_insight.slug}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <EngineStatusBadge status={request.engine_status} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {format(
                              new Date(request.created_at),
                              "MMM d, yyyy HH:mm",
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <Link
                              href={bizminer_admin_insight_request_path(
                                request.id,
                              )}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {pagy.last > 1 && <PaginationControls pagy={pagy} />}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </BizminerAdminLayout>
  );
}
